import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Switch } from './ui/switch';
import { Plus, X, Pencil, Check, Mail, ClipboardList, ChevronUp, ChevronDown, Archive, Database, GraduationCap, Hash, FileText, Trash2, GripVertical, ClipboardCheck, Zap, Shield, Bell, Languages, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import UserManagement from './UserManagement';
import EmailSettingsLegacy from './EmailSettings';
import ProfileChangeRequests from './ProfileChangeRequests';
import ArchivedItemsTab from './ArchivedItemsTab';
import DatabaseBackupTab from './DatabaseBackupTab';
import { HeaderLogoCleanupCard } from './HeaderLogoCleanupCard';
import TemplateBackupPanel from './TemplateBackupPanel';
import TrainingTemplatesSettings from './TrainingTemplatesSettings';
import NumberingFormatsSettings from './NumberingFormatsSettings';
import SavedInvoiceItems from './invoices/SavedInvoiceItems';
import EmailSettings from './invoices/EmailSettings';
import ChecklistTemplateSettings from './ChecklistTemplateSettings';
import TestReportTemplateSettings from './TestReportTemplateSettings';
import TaskTemplateSettings from './TaskTemplateSettings';
import TranslationsSettings from './TranslationsSettings';
import RolePermissionsMatrix from './RolePermissionsMatrix';
import ApplicationDefaults from './ApplicationDefaults';
import PhotoCompressionSettings from './PhotoCompressionSettings';
import ValueTranslationsSettings from './ValueTranslationsSettings';
import RemindersSettings from './RemindersSettings';
import LoginAuditLog from './LoginAuditLog';
import { AdminFilesPanel } from './AdminFilesPanel';
import { OrphanFileCleanupCard } from './OrphanFileCleanupCard';
import { RestoreFromBackupCard } from './RestoreFromBackupCard';
import { CategoryVisibilityDefaults } from './CategoryVisibilityDefaults';
import { isAdminUser } from './FileVisibility';
import { useAuth } from '../contexts/AuthContext';
import useSubTabShortcut from '../hooks/useSubTabShortcut';

const SETTINGS_SUBTAB_LABELS = {
  general: 'settings.tabs.general', users: 'settings.tabs.users', roles: 'settings.tabs.roles',
  'profile-changes': 'settings.tabs.profileChanges', project: 'settings.tabs.project',
  certificates: 'settings.tabs.certificates', expenses: 'settings.tabs.expenses',
  'training-templates': 'settings.tabs.training', numbering: 'settings.tabs.numbering',
  'invoice-items': 'settings.tabs.invoiceItems', email: 'settings.tabs.email', archived: 'settings.tabs.archived',
  'doc-categories': 'settings.tabs.docCategories', 'checklist-templates': 'settings.tabs.checklists',
  'test-report-templates': 'settings.tabs.testReports', 'task-templates': 'settings.tabs.tasks',
  backup: 'settings.tabs.backup', 'template-backup': 'settings.tabs.templateBackup',
  reminders: 'settings.tabs.reminders', translations: 'settings.tabs.translations',
  'login-audit': 'settings.tabs.loginAudit', files: 'fileVisibility.tabTitle',
};
import usePermissions from '../hooks/usePermissions';
import { SETTINGS_TABS } from '../constants/settingsTabs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


// ==================== Document Categories Settings ====================
const DocumentCategoriesSettings = () => {
  const { t } = useTranslation();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newValue, setNewValue] = useState('');
  const [newLabel, setNewLabel] = useState('');

  const fetchCategories = async () => {
    try {
      const resp = await axios.get(`${API}/settings/document-categories`);
      setCategories(resp.data.categories || []);
    } catch { /* ignore */ } finally { setLoading(false); }
  };

  useEffect(() => { fetchCategories(); }, []);

  const saveCategories = async (updated) => {
    try {
      await axios.put(`${API}/settings/document-categories`, { categories: updated });
      setCategories(updated);
      toast.success(t('auto.set.categoriesSaved'));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  };

  const addCategory = () => {
    if (!newValue.trim() || !newLabel.trim()) { toast.error(t('auto.set.bothValueAndLabelAreRequired')); return; }
    const slug = newValue.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    if (categories.some(c => c.value === slug)) { toast.error(t('auto.set.categoryAlreadyExists')); return; }
    saveCategories([...categories, { value: slug, label: newLabel.trim() }]);
    setNewValue('');
    setNewLabel('');
  };

  const removeCategory = (idx) => {
    saveCategories(categories.filter((_, i) => i !== idx));
  };

  const moveCategory = (idx, dir) => {
    const arr = [...categories];
    const target = idx + dir;
    if (target < 0 || target >= arr.length) return;
    [arr[idx], arr[target]] = [arr[target], arr[idx]];
    saveCategories(arr);
  };

  if (loading) return <div className="text-sm text-gray-400 p-4">{t('auto.set.loading2')}</div>;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <FileText className="h-5 w-5" />
          {t('auto.set.isoDocumentCategories')}
        </CardTitle>
        <CardDescription>
          {t('auto.set.manageCategoriesUsedForIso9001')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-1">
          {categories.map((cat, idx) => (
            <div key={cat.value} className="flex items-center gap-2 p-2 rounded hover:bg-gray-50 group" data-testid={`doc-cat-row-${cat.value}`}>
              <div className="flex flex-col gap-0.5">
                <button onClick={() => moveCategory(idx, -1)} disabled={idx === 0} className="text-gray-300 hover:text-gray-600 disabled:opacity-30"><ChevronUp className="h-3 w-3" /></button>
                <button onClick={() => moveCategory(idx, 1)} disabled={idx === categories.length - 1} className="text-gray-300 hover:text-gray-600 disabled:opacity-30"><ChevronDown className="h-3 w-3" /></button>
              </div>
              <span className="text-sm font-medium flex-1">{cat.label}</span>
              <span className="text-xs text-gray-400 font-mono">{cat.value}</span>
              <button onClick={() => removeCategory(idx)} className="text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`remove-doc-cat-${cat.value}`}>
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
        <div className="flex gap-2 items-end pt-2 border-t">
          <div className="flex-1">
            <Label className="text-xs">{t('auto.set.labelDisplayName')}</Label>
            <Input value={newLabel} onChange={(e) => { setNewLabel(e.target.value); setNewValue(e.target.value.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '')); }} placeholder="e.g. Work Instruction" data-testid="new-doc-cat-label" />
          </div>
          <div className="w-40">
            <Label className="text-xs">{t('auto.set.valueSlug')}</Label>
            <Input value={newValue} onChange={(e) => setNewValue(e.target.value)} placeholder="work_instruction" className="font-mono text-xs" data-testid="new-doc-cat-value" />
          </div>
          <Button size="sm" onClick={addCategory} data-testid="add-doc-cat-btn"><Plus className="h-4 w-4 mr-1" /> {t('auto.set.add')}</Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Category Section Component (for general settings like departments, positions, etc.)
const CategorySection = ({ title, category, items, newItem, setNewItem, onAdd, onRemove, onMoveUp, onMoveDown }) => {
  const { t } = useTranslation();
  return (
  <Card>
    <CardHeader>
      <CardTitle className="text-lg">{title}</CardTitle>
    </CardHeader>
    <CardContent className="space-y-3">
      <div className="flex gap-2">
        <Input
          placeholder={t('settings.general.addNewNamed', { name: title.toLowerCase() })}
          value={newItem[category] || ''}
          onChange={(e) => setNewItem(prev => ({ ...prev, [category]: e.target.value  }))}
          onKeyPress={(e) => e.key === 'Enter' && onAdd(category)}
        />
        <Button onClick={() => onAdd(category)} size="sm">
          <Plus className="w-4 h-4" />
        </Button>
      </div>
      <div className="space-y-2">
        {items && items.map((item, index) => (
          <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
            <span>{item}</span>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onMoveUp(category, index)}
                disabled={index === 0}
                title={t('auto.set.moveUp')}
              >
                <ChevronUp className="w-4 h-4 text-gray-500" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onMoveDown(category, index)}
                disabled={index === items.length - 1}
                title={t('auto.set.moveDown')}
              >
                <ChevronDown className="w-4 h-4 text-gray-500" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onRemove(category, index)}
              >
                <X className="w-4 h-4 text-red-500" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </CardContent>
  </Card>
  );
};

// Certificate Section Component
const CertificateSection = ({ title, certCategory, types, newItem, setNewItem, onAdd, onRemove, onMoveUp, onMoveDown }) => (
  <Card>
    <CardHeader>
      <CardTitle className="text-lg">{title}</CardTitle>
    </CardHeader>
    <CardContent className="space-y-3">
      <div className="flex gap-2">
        <Input
          placeholder={`Add new ${title.toLowerCase()} type`}
          value={newItem[`cert_${certCategory}`] || ''}
          onChange={(e) => setNewItem(prev => ({ ...prev, [`cert_${certCategory}`]: e.target.value  }))}
          onKeyPress={(e) => e.key === 'Enter' && onAdd(certCategory)}
        />
        <Button onClick={() => onAdd(certCategory)} size="sm">
          <Plus className="w-4 h-4" />
        </Button>
      </div>
      <div className="space-y-2">
        {types && types.map((type, index) => (
          <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
            <span>{type}</span>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onMoveUp(certCategory, index)}
                disabled={index === 0}
                title={i18n.t('auto.set.moveUp')}
              >
                <ChevronUp className="w-4 h-4 text-gray-500" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onMoveDown(certCategory, index)}
                disabled={index === types.length - 1}
                title={i18n.t('auto.set.moveDown')}
              >
                <ChevronDown className="w-4 h-4 text-gray-500" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onRemove(certCategory, index)}
              >
                <X className="w-4 h-4 text-red-500" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </CardContent>
  </Card>
);

// Expense Category Section Component
const ExpenseCategorySection = ({
  expenseCategory,
  displayName,
  subcategories,
  visible,
  newItem,
  setNewItem,
  isEditing,
  editDisplayName,
  setEditDisplayName,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onToggleVisibility,
  onAddSubcategory,
  onRemoveSubcategory,
  onMoveSubcategoryUp,
  onMoveSubcategoryDown
}) => (
  <Card>
    <CardHeader>
      <div className="flex items-center justify-between">
        <div className="flex-1">
          {isEditing ? (
            <div className="flex items-center gap-2">
              <Input
                value={editDisplayName}
                onChange={(e) => setEditDisplayName(e.target.value)}
                className="h-8 text-lg font-semibold"
                onKeyPress={(e) => e.key === 'Enter' && onSaveEdit(expenseCategory)}
              />
              <Button size="sm" variant="ghost" onClick={() => onSaveEdit(expenseCategory)}>
                <Check className="w-4 h-4 text-green-600" />
              </Button>
              <Button size="sm" variant="ghost" onClick={onCancelEdit} data-testid="btn-canceledit">
                <X className="w-4 h-4 text-gray-500" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <CardTitle className="text-lg">{displayName}</CardTitle>
              <Button size="sm" variant="ghost" onClick={() => onStartEdit(expenseCategory)}>
                <Pencil className="w-3 h-3 text-gray-500" />
              </Button>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Label htmlFor={`visible-${expenseCategory}`} className="text-sm text-gray-500">
            {i18n.t('auto.set.showInExpenses')}
          </Label>
          <Switch
            id={`visible-${expenseCategory}`}
            checked={visible}
            onCheckedChange={() => onToggleVisibility(expenseCategory)}
          />
        </div>
      </div>
      <CardDescription className="text-xs">
        Internal key: {expenseCategory}
      </CardDescription>
    </CardHeader>
    <CardContent className="space-y-3">
      <div className="flex gap-2">
        <Input
          placeholder={`Add new ${displayName.toLowerCase()} type`}
          value={newItem[`expense_${expenseCategory}`] || ''}
          onChange={(e) => setNewItem(prev => ({ ...prev, [`expense_${expenseCategory}`]: e.target.value  }))}
          onKeyPress={(e) => e.key === 'Enter' && onAddSubcategory(expenseCategory)}
        />
        <Button onClick={() => onAddSubcategory(expenseCategory)} size="sm">
          <Plus className="w-4 h-4" />
        </Button>
      </div>
      <div className="space-y-2">
        {subcategories && subcategories.map((subcat, index) => (
          <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
            <span>{subcat}</span>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onMoveSubcategoryUp(expenseCategory, index)}
                disabled={index === 0}
                title={i18n.t('auto.set.moveUp')}
              >
                <ChevronUp className="w-4 h-4 text-gray-500" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onMoveSubcategoryDown(expenseCategory, index)}
                disabled={index === subcategories.length - 1}
                title={i18n.t('auto.set.moveDown')}
              >
                <ChevronDown className="w-4 h-4 text-gray-500" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onRemoveSubcategory(expenseCategory, index)}
              >
                <X className="w-4 h-4 text-red-500" />
              </Button>
            </div>
          </div>
        ))}
        {(!subcategories || subcategories.length === 0) && (
          <p className="text-sm text-gray-400 italic">{i18n.t('auto.set.noSubcategoriesDefined')}</p>
        )}
      </div>
    </CardContent>
  </Card>
);

const SettingsTab = ({ onSettingsChange }) => {
  const { t } = useTranslation();
  const { can } = usePermissions();
  const { user: me } = useAuth();
  const isAdmin = isAdminUser(me);
  // Per-sub-tab visibility lookup. Memoised so it re-evaluates when the
  // permission set changes (e.g. after a roles-matrix save).
  const isVisible = (key) => {
    const tab = SETTINGS_TABS.find((t) => t.key === key);
    return tab ? can(`${tab.permKey}.view`) : false;
  };
  // Default to the first tab the current user can actually see, so a
  // user with only "Numbering" doesn't land on a hidden Users tab.
  const firstVisible = SETTINGS_TABS.find((t) => can(`${t.permKey}.view`))?.key || 'users';
  const [pickedSubTab, setPickedSubTab] = useState(null);
  const settingsSubTab = pickedSubTab || firstVisible;
  useSubTabShortcut({
    tab: 'settings', ctxKey: 'settingsSubTab', value: settingsSubTab, setValue: setPickedSubTab,
    labels: SETTINGS_SUBTAB_LABELS,
    allowed: [...SETTINGS_TABS.filter((x) => can(`${x.permKey}.view`)).map((x) => x.key), ...(isAdmin ? ['files'] : [])],
    perms: (v) => {
      const tab = SETTINGS_TABS.find((x) => x.key === v);
      return tab ? [`${tab.permKey}.view`] : [];
    },
  });
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newItem, setNewItem] = useState({});
  const [editingCategory, setEditingCategory] = useState(null);
  const [editDisplayName, setEditDisplayName] = useState('');

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
      if (onSettingsChange) {
        onSettingsChange(response.data);
      }
    } catch (error) {
      toast.error(t('auto.set.errorLoadingSettings'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const addItem = async (category) => {
    if (!newItem[category] || newItem[category].trim() === '') {
      toast.error(t('auto.set.pleaseEnterAValue'));
      return;
    }

    try {
      const currentItems = settings[category] || [];
      const updatedItems = [...currentItems, newItem[category].trim()];
      
      await axios.put(`${API}/settings/${category}`, updatedItems);
      toast.success(t('auto.set.itemAddedSuccessfully'));
      setNewItem(prev => ({ ...prev, [category]: ''  }));
      fetchSettings();
    } catch (error) {
      toast.error(t('auto.set.errorAddingItem'));
    }
  };

  const removeItem = async (category, index) => {
    try {
      const currentItems = settings[category] || [];
      const updatedItems = currentItems.filter((_, i) => i !== index);
      
      await axios.put(`${API}/settings/${category}`, updatedItems);
      toast.success(t('auto.set.itemRemovedSuccessfully'));
      fetchSettings();
    } catch (error) {
      toast.error(t('auto.set.errorRemovingItem'));
    }
  };

  // Move item up in list (general settings)
  const moveItemUp = async (category, index) => {
    if (index === 0) return;
    try {
      const currentItems = [...(settings[category] || [])];
      [currentItems[index - 1], currentItems[index]] = [currentItems[index], currentItems[index - 1]];
      
      await axios.put(`${API}/settings/${category}`, currentItems);
      toast.success(t('auto.set.itemMovedUp'));
      fetchSettings();
    } catch (error) {
      toast.error(t('auto.set.errorMovingItem'));
    }
  };

  // Move item down in list (general settings)
  const moveItemDown = async (category, index) => {
    const currentItems = settings[category] || [];
    if (index >= currentItems.length - 1) return;
    try {
      const items = [...currentItems];
      [items[index], items[index + 1]] = [items[index + 1], items[index]];
      
      await axios.put(`${API}/settings/${category}`, items);
      toast.success(t('auto.set.itemMovedDown'));
      fetchSettings();
    } catch (error) {
      toast.error(t('auto.set.errorMovingItem'));
    }
  };

  const addCertType = async (certCategory) => {
    const key = `cert_${certCategory}`;
    if (!newItem[key] || newItem[key].trim() === '') {
      toast.error(t('auto.set.pleaseEnterACertificateType'));
      return;
    }

    try {
      const currentTypes = settings.certificate_types?.[certCategory] || [];
      const updatedTypes = [...currentTypes, newItem[key].trim()];
      
      await axios.put(`${API}/settings/certificate_types/${certCategory}`, updatedTypes);
      toast.success(t('auto.set.certificateTypeAddedSuccessfully'));
      setNewItem(prev => ({ ...prev, [key]: ''  }));
      fetchSettings();
    } catch (error) {
      toast.error(t('auto.set.errorAddingCertificateType'));
    }
  };

  const removeCertType = async (certCategory, index) => {
    try {
      const currentTypes = settings.certificate_types?.[certCategory] || [];
      const updatedTypes = currentTypes.filter((_, i) => i !== index);
      
      await axios.put(`${API}/settings/certificate_types/${certCategory}`, updatedTypes);
      toast.success(t('auto.set.certificateTypeRemovedSuccessfully'));
      fetchSettings();
    } catch (error) {
      toast.error(t('auto.set.errorRemovingCertificateType'));
    }
  };

  // Move certificate type up
  const moveCertTypeUp = async (certCategory, index) => {
    if (index === 0) return;
    try {
      const currentTypes = [...(settings.certificate_types?.[certCategory] || [])];
      [currentTypes[index - 1], currentTypes[index]] = [currentTypes[index], currentTypes[index - 1]];
      
      await axios.put(`${API}/settings/certificate_types/${certCategory}`, currentTypes);
      toast.success(t('auto.set.certificateTypeMovedUp'));
      fetchSettings();
    } catch (error) {
      toast.error(t('auto.set.errorMovingCertificateType'));
    }
  };

  // Move certificate type down
  const moveCertTypeDown = async (certCategory, index) => {
    const currentTypes = settings.certificate_types?.[certCategory] || [];
    if (index >= currentTypes.length - 1) return;
    try {
      const types = [...currentTypes];
      [types[index], types[index + 1]] = [types[index + 1], types[index]];
      
      await axios.put(`${API}/settings/certificate_types/${certCategory}`, types);
      toast.success(t('auto.set.certificateTypeMovedDown'));
      fetchSettings();
    } catch (error) {
      toast.error(t('auto.set.errorMovingCertificateType'));
    }
  };

  // Get subcategories from new format (handles both old array and new object format)
  const getSubcategories = (category) => {
    const cat = settings?.expense_categories?.[category];
    if (!cat) return [];
    if (Array.isArray(cat)) return cat;
    return cat.subcategories || [];
  };

  // Get display name from new format
  const getDisplayName = (category) => {
    const cat = settings?.expense_categories?.[category];
    if (!cat) return category;
    if (Array.isArray(cat)) return category.charAt(0).toUpperCase() + category.slice(1);
    return cat.display_name || category;
  };

  // Get visibility from new format
  const getVisibility = (category) => {
    const cat = settings?.expense_categories?.[category];
    if (!cat) return true;
    if (Array.isArray(cat)) return true;
    return cat.visible !== false;
  };

  const addExpenseCategory = async (expenseCategory) => {
    const key = `expense_${expenseCategory}`;
    if (!newItem[key] || newItem[key].trim() === '') {
      toast.error(t('auto.set.pleaseEnterAnExpenseSubcategory'));
      return;
    }

    try {
      const currentSubs = getSubcategories(expenseCategory);
      const updatedSubs = [...currentSubs, newItem[key].trim()];
      
      await axios.put(`${API}/settings/expense_categories/${expenseCategory}`, updatedSubs);
      toast.success(t('auto.set.expenseSubcategoryAddedSuccessfully'));
      setNewItem(prev => ({ ...prev, [key]: ''  }));
      fetchSettings();
    } catch (error) {
      toast.error(t('auto.set.errorAddingExpenseSubcategory'));
    }
  };

  const removeExpenseCategory = async (expenseCategory, index) => {
    try {
      const currentSubs = getSubcategories(expenseCategory);
      const updatedSubs = currentSubs.filter((_, i) => i !== index);
      
      await axios.put(`${API}/settings/expense_categories/${expenseCategory}`, updatedSubs);
      toast.success(t('auto.set.expenseSubcategoryRemovedSuccessfully'));
      fetchSettings();
    } catch (error) {
      toast.error(t('auto.set.errorRemovingExpenseSubcategory'));
    }
  };

  // Move expense subcategory up
  const moveExpenseSubcategoryUp = async (expenseCategory, index) => {
    if (index === 0) return;
    try {
      const currentSubs = [...getSubcategories(expenseCategory)];
      [currentSubs[index - 1], currentSubs[index]] = [currentSubs[index], currentSubs[index - 1]];
      
      await axios.put(`${API}/settings/expense_categories/${expenseCategory}`, currentSubs);
      toast.success(t('auto.set.subcategoryMovedUp'));
      fetchSettings();
    } catch (error) {
      toast.error(t('auto.set.errorMovingSubcategory'));
    }
  };

  // Move expense subcategory down
  const moveExpenseSubcategoryDown = async (expenseCategory, index) => {
    const currentSubs = getSubcategories(expenseCategory);
    if (index >= currentSubs.length - 1) return;
    try {
      const subs = [...currentSubs];
      [subs[index], subs[index + 1]] = [subs[index + 1], subs[index]];
      
      await axios.put(`${API}/settings/expense_categories/${expenseCategory}`, subs);
      toast.success(t('auto.set.subcategoryMovedDown'));
      fetchSettings();
    } catch (error) {
      toast.error(t('auto.set.errorMovingSubcategory'));
    }
  };

  const updateCategorySettings = async (expenseCategory, displayName, visible) => {
    try {
      await axios.put(`${API}/settings/expense_categories/${expenseCategory}/settings`, {
        display_name: displayName,
        visible: visible
      });
      toast.success(t('auto.set.categorySettingsUpdated'));
      fetchSettings();
    } catch (error) {
      toast.error(t('auto.set.errorUpdatingCategorySettings'));
    }
  };

  const handleVisibilityToggle = async (expenseCategory) => {
    const currentVisible = getVisibility(expenseCategory);
    const displayName = getDisplayName(expenseCategory);
    await updateCategorySettings(expenseCategory, displayName, !currentVisible);
  };

  const startEditingDisplayName = (category) => {
    setEditingCategory(category);
    setEditDisplayName(getDisplayName(category));
  };

  const saveDisplayName = async (category) => {
    if (!editDisplayName.trim()) {
      toast.error(t('auto.set.displayNameCannotBeEmpty'));
      return;
    }
    const currentVisible = getVisibility(category);
    await updateCategorySettings(category, editDisplayName.trim(), currentVisible);
    setEditingCategory(null);
    setEditDisplayName('');
  };

  const cancelEditDisplayName = () => {
    setEditingCategory(null);
    setEditDisplayName('');
  };

  if (loading) {
    return <div className="p-8 text-center">{t('auto.set.loadingSettings')}</div>;
  }

  if (!settings) {
    return <div className="p-8 text-center">{t('auto.set.errorLoadingSettings')}</div>;
  }

  const expenseCategories = ['travel', 'accommodation', 'tools', 'consumables', 'meals', 'other'];

  return (
    <div className="p-6 space-y-6">
      <div>
        <h2 className="text-2xl font-bold">{t('settings.title')}</h2>
        <p className="text-gray-600">{t('settings.subtitle')}</p>
      </div>

      <Tabs value={settingsSubTab} onValueChange={setPickedSubTab} className="space-y-4">
        <TabsList className="flex flex-wrap h-auto gap-1 p-1">
          {isVisible('general') && (
          <TabsTrigger value="general" className="px-3 py-2" data-testid="tab-general">{t('settings.tabs.general')}</TabsTrigger>
          )}
          {isVisible('users') && (
          <TabsTrigger value="users" className="px-3 py-2" data-testid="tab-users">{t('settings.tabs.users')}</TabsTrigger>
          )}
          {isVisible('roles') && (
          <TabsTrigger value="roles" className="px-3 py-2 flex items-center gap-1" data-testid="roles-permissions-tab">
            <Shield className="h-4 w-4" />
            {t('settings.tabs.roles')}
          </TabsTrigger>
          )}
          {isVisible('profile-changes') && (
          <TabsTrigger value="profile-changes" className="px-3 py-2 flex items-center gap-2" data-testid="tab-profile-changes">
            <ClipboardList className="h-4 w-4" />
            {t('settings.tabs.profileChanges')}
          </TabsTrigger>
          )}
          {isVisible('project') && (
          <TabsTrigger value="project" className="px-3 py-2" data-testid="tab-project">{t('settings.tabs.project')}</TabsTrigger>
          )}
          {isVisible('certificates') && (
          <TabsTrigger value="certificates" className="px-3 py-2" data-testid="tab-certificates">{t('settings.tabs.certificates')}</TabsTrigger>
          )}
          {isVisible('expenses') && (
          <TabsTrigger value="expenses" className="px-3 py-2" data-testid="tab-expenses">{t('settings.tabs.expenses')}</TabsTrigger>
          )}
          {isVisible('training-templates') && (
          <TabsTrigger value="training-templates" className="px-3 py-2 flex items-center gap-1" data-testid="training-templates-tab">
            <GraduationCap className="h-4 w-4" />
            {t('settings.tabs.training')}
          </TabsTrigger>
          )}
          {isVisible('numbering') && (
          <TabsTrigger value="numbering" className="px-3 py-2 flex items-center gap-1" data-testid="numbering-tab">
            <Hash className="h-4 w-4" />
            {t('settings.tabs.numbering')}
          </TabsTrigger>
          )}
          {isVisible('invoice-items') && (
          <TabsTrigger value="invoice-items" className="px-3 py-2 flex items-center gap-1" data-testid="invoice-items-tab">
            <ClipboardList className="h-4 w-4" />
            {t('settings.tabs.invoiceItems')}
          </TabsTrigger>
          )}
          {isVisible('email') && (
          <TabsTrigger value="email" className="px-3 py-2 flex items-center gap-1" data-testid="email-tab">
            <Mail className="h-4 w-4" />
            {t('settings.tabs.email')}
          </TabsTrigger>
          )}
          {isVisible('archived') && (
          <TabsTrigger value="archived" className="px-3 py-2 flex items-center gap-1" data-testid="archived-items-tab-trigger">
            <Archive className="h-4 w-4" />
            {t('settings.tabs.archived')}
          </TabsTrigger>
          )}
          {isVisible('doc-categories') && (
          <TabsTrigger value="doc-categories" className="px-3 py-2 flex items-center gap-1" data-testid="doc-categories-tab">
            <FileText className="h-4 w-4" />
            {t('settings.tabs.docCategories')}
          </TabsTrigger>
          )}
          {isVisible('checklist-templates') && (
          <TabsTrigger value="checklist-templates" className="px-3 py-2 flex items-center gap-1" data-testid="checklist-templates-tab">
            <ClipboardCheck className="h-4 w-4" />
            {t('settings.tabs.checklists')}
          </TabsTrigger>
          )}
          {isVisible('test-report-templates') && (
          <TabsTrigger value="test-report-templates" className="px-3 py-2 flex items-center gap-1" data-testid="test-report-templates-tab">
            <Zap className="h-4 w-4" />
            {t('settings.tabs.testReports')}
          </TabsTrigger>
          )}
          {isVisible('task-templates') && (
          <TabsTrigger value="task-templates" className="px-3 py-2 flex items-center gap-1" data-testid="task-templates-tab">
            <ClipboardList className="h-4 w-4" />
            {t('settings.tabs.tasks')}
          </TabsTrigger>
          )}
          {isVisible('backup') && (
          <TabsTrigger value="backup" className="px-3 py-2 flex items-center gap-1" data-testid="backup-tab-trigger">
            <Database className="h-4 w-4" />
            {t('settings.tabs.backup')}
          </TabsTrigger>
          )}
          {isVisible('backup') && (
          <TabsTrigger value="template-backup" className="px-3 py-2 flex items-center gap-1" data-testid="template-backup-tab-trigger">
            <Database className="h-4 w-4" />
            {t('settings.tabs.templateBackup')}
          </TabsTrigger>
          )}
          {isVisible('reminders') && (
          <TabsTrigger value="reminders" className="px-3 py-2 flex items-center gap-1" data-testid="reminders-tab-trigger">
            <Bell className="h-4 w-4" />
            {t('settings.tabs.reminders')}
          </TabsTrigger>
          )}
          {isVisible('translations') && (
          <TabsTrigger value="translations" className="px-3 py-2 flex items-center gap-1" data-testid="translations-tab-trigger">
            <Languages className="h-4 w-4" />
            {t('settings.tabs.translations')}
          </TabsTrigger>
          )}
          {isVisible('login-audit') && (
          <TabsTrigger value="login-audit" className="px-3 py-2 flex items-center gap-1" data-testid="login-audit-tab-trigger">
            <ShieldCheck className="h-4 w-4" />
            {t('settings.tabs.loginAudit')}
          </TabsTrigger>
          )}
          {isAdmin && (
          <TabsTrigger value="files" className="px-3 py-2 flex items-center gap-1" data-testid="files-tab-trigger">
            <FileText className="h-4 w-4" />
            {t('fileVisibility.tabTitle')}
          </TabsTrigger>
          )}
        </TabsList>

        {isAdmin && (
        <TabsContent value="files" className="space-y-4">
          <CategoryVisibilityDefaults />
          <AdminFilesPanel />
          <OrphanFileCleanupCard />
          <RestoreFromBackupCard />
        </TabsContent>
        )}

        <TabsContent value="login-audit" className="space-y-4">
          <LoginAuditLog />
        </TabsContent>

        <TabsContent value="users" className="space-y-4">
          <UserManagement />
        </TabsContent>

        <TabsContent value="roles" className="space-y-4">
          <RolePermissionsMatrix />
        </TabsContent>

        <TabsContent value="profile-changes" className="space-y-4">
          <ProfileChangeRequests />
        </TabsContent>

        <TabsContent value="email" className="space-y-4">
          <EmailSettings />
        </TabsContent>

        <TabsContent value="general" className="space-y-4">
          <ApplicationDefaults canEdit={can('settings_general.edit')} />
          <PhotoCompressionSettings canEdit={can('settings_general.edit')} />
          <ValueTranslationsSettings canEdit={can('settings_general.edit')} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <CategorySection title={t('settings.general.departments')} category="departments" items={settings.departments} newItem={newItem} setNewItem={setNewItem} onAdd={addItem} onRemove={removeItem} onMoveUp={moveItemUp} onMoveDown={moveItemDown} />
            <CategorySection title={t('settings.general.positions')} category="positions" items={settings.positions} newItem={newItem} setNewItem={setNewItem} onAdd={addItem} onRemove={removeItem} onMoveUp={moveItemUp} onMoveDown={moveItemDown} />
            <CategorySection title={t('settings.general.countries')} category="countries" items={settings.countries} newItem={newItem} setNewItem={setNewItem} onAdd={addItem} onRemove={removeItem} onMoveUp={moveItemUp} onMoveDown={moveItemDown} />
            <CategorySection title={t('settings.general.currencies')} category="currencies" items={settings.currencies} newItem={newItem} setNewItem={setNewItem} onAdd={addItem} onRemove={removeItem} onMoveUp={moveItemUp} onMoveDown={moveItemDown} />
            <CategorySection title={t('settings.general.relationships')} category="relationships" items={settings.relationships} newItem={newItem} setNewItem={setNewItem} onAdd={addItem} onRemove={removeItem} onMoveUp={moveItemUp} onMoveDown={moveItemDown} />
            <CategorySection title={t('auto.set.documentTypesAttachments')} category="attachment_categories" items={settings.attachment_categories} newItem={newItem} setNewItem={setNewItem} onAdd={addItem} onRemove={removeItem} onMoveUp={moveItemUp} onMoveDown={moveItemDown} />
            <CategorySection title={t('auto.set.educationLevels')} category="education_levels" items={settings.education_levels} newItem={newItem} setNewItem={setNewItem} onAdd={addItem} onRemove={removeItem} onMoveUp={moveItemUp} onMoveDown={moveItemDown} />
            <CategorySection title={t('auto.set.occupationsTrades')} category="occupations" items={settings.occupations} newItem={newItem} setNewItem={setNewItem} onAdd={addItem} onRemove={removeItem} onMoveUp={moveItemUp} onMoveDown={moveItemDown} />
            <CategorySection title={t('settings.general.pkdNumbers')} category="pkd_numbers" items={settings.pkd_numbers} newItem={newItem} setNewItem={setNewItem} onAdd={addItem} onRemove={removeItem} onMoveUp={moveItemUp} onMoveDown={moveItemDown} />
          </div>
        </TabsContent>

        <TabsContent value="project" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <CategorySection title={t('auto.set.projectRoles')} category="project_roles" items={settings.project_roles} newItem={newItem} setNewItem={setNewItem} onAdd={addItem} onRemove={removeItem} onMoveUp={moveItemUp} onMoveDown={moveItemDown} />
            <CategorySection title={t('auto.set.euVisaTypes')} category="eu_visa_types" items={settings.eu_visa_types} newItem={newItem} setNewItem={setNewItem} onAdd={addItem} onRemove={removeItem} onMoveUp={moveItemUp} onMoveDown={moveItemDown} />
            <CategorySection title={t('auto.set.usVisaTypes')} category="us_visa_types" items={settings.us_visa_types} newItem={newItem} setNewItem={setNewItem} onAdd={addItem} onRemove={removeItem} onMoveUp={moveItemUp} onMoveDown={moveItemDown} />
            <CategorySection title={t('auto.set.ukWorkPermitTypes')} category="uk_work_permit_types" items={settings.uk_work_permit_types} newItem={newItem} setNewItem={setNewItem} onAdd={addItem} onRemove={removeItem} onMoveUp={moveItemUp} onMoveDown={moveItemDown} />
          </div>
        </TabsContent>

        <TabsContent value="certificates" className="space-y-4">
          <p className="text-gray-600">{t('auto.set.configureCertificateTypesForEmployeeQualification')}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <CertificateSection title={t('auto.set.electricalCertificateTypes')} certCategory="electrical" types={settings.certificate_types?.electrical} newItem={newItem} setNewItem={setNewItem} onAdd={addCertType} onRemove={removeCertType} onMoveUp={moveCertTypeUp} onMoveDown={moveCertTypeDown} />
            <CertificateSection title={t('auto.set.scissorLiftCertificateTypes')} certCategory="scissor_lift" types={settings.certificate_types?.scissor_lift} newItem={newItem} setNewItem={setNewItem} onAdd={addCertType} onRemove={removeCertType} onMoveUp={moveCertTypeUp} onMoveDown={moveCertTypeDown} />
            <CertificateSection title={t('auto.set.boomLiftCertificateTypes')} certCategory="boom_lift" types={settings.certificate_types?.boom_lift} newItem={newItem} setNewItem={setNewItem} onAdd={addCertType} onRemove={removeCertType} onMoveUp={moveCertTypeUp} onMoveDown={moveCertTypeDown} />
            <CertificateSection title={t('auto.set.oshaCertificateTypes')} certCategory="osha" types={settings.certificate_types?.osha} newItem={newItem} setNewItem={setNewItem} onAdd={addCertType} onRemove={removeCertType} onMoveUp={moveCertTypeUp} onMoveDown={moveCertTypeDown} />
            <CertificateSection title={t('auto.set.healthSafetyCertificateTypes')} certCategory="health_safety" types={settings.certificate_types?.health_safety} newItem={newItem} setNewItem={setNewItem} onAdd={addCertType} onRemove={removeCertType} onMoveUp={moveCertTypeUp} onMoveDown={moveCertTypeDown} />
          </div>
        </TabsContent>

        <TabsContent value="expenses" className="space-y-4">
          <p className="text-gray-600">{t('auto.set.configureExpenseCategoriesForProjectBudgeting')}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {expenseCategories.map(cat => (
              <ExpenseCategorySection
                key={cat}
                expenseCategory={cat}
                displayName={getDisplayName(cat)}
                subcategories={getSubcategories(cat)}
                visible={getVisibility(cat)}
                newItem={newItem}
                setNewItem={setNewItem}
                isEditing={editingCategory === cat}
                editDisplayName={editDisplayName}
                setEditDisplayName={setEditDisplayName}
                onStartEdit={startEditingDisplayName}
                onSaveEdit={saveDisplayName}
                onCancelEdit={cancelEditDisplayName}
                onToggleVisibility={handleVisibilityToggle}
                onAddSubcategory={addExpenseCategory}
                onRemoveSubcategory={removeExpenseCategory}
                onMoveSubcategoryUp={moveExpenseSubcategoryUp}
                onMoveSubcategoryDown={moveExpenseSubcategoryDown}
              />
            ))}
          </div>
          
          {/* Payment Methods */}
          <div className="mt-6 pt-6 border-t">
            <h3 className="text-lg font-semibold mb-2">{t('auto.set.paymentMethods')}</h3>
            <p className="text-gray-600 text-sm mb-4">{t('auto.set.definePaymentMethodsThatCanBe')}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <CategorySection 
                title={t('auto.set.paymentMethods')} 
                category="payment_methods" 
                items={settings.payment_methods || []} 
                newItem={newItem} 
                setNewItem={setNewItem} 
                onAdd={addItem} 
                onRemove={removeItem} 
                onMoveUp={moveItemUp} 
                onMoveDown={moveItemDown} 
              />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="training-templates" className="space-y-4">
          <TrainingTemplatesSettings />
        </TabsContent>

        <TabsContent value="numbering" className="space-y-4">
          <NumberingFormatsSettings />
        </TabsContent>

        <TabsContent value="invoice-items" className="space-y-4">
          <SavedInvoiceItems />
        </TabsContent>

        <TabsContent value="archived" className="space-y-4">
          <ArchivedItemsTab />
        </TabsContent>

        <TabsContent value="backup" className="space-y-4">
          <DatabaseBackupTab />
          <HeaderLogoCleanupCard />
        </TabsContent>

        <TabsContent value="template-backup" className="space-y-4">
          <TemplateBackupPanel />
        </TabsContent>

        <TabsContent value="doc-categories" className="space-y-4">
          <DocumentCategoriesSettings />
        </TabsContent>

        <TabsContent value="checklist-templates" className="space-y-4">
          <ChecklistTemplateSettings />
        </TabsContent>

        <TabsContent value="test-report-templates" className="space-y-4">
          <TestReportTemplateSettings />
        </TabsContent>

        <TabsContent value="task-templates" className="space-y-4">
          <TaskTemplateSettings />
        </TabsContent>

        <TabsContent value="reminders" className="space-y-4">
          <RemindersSettings canEdit={can('settings_reminders.edit')} />
        </TabsContent>

        <TabsContent value="translations" className="space-y-4">
          <TranslationsSettings />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SettingsTab;
